<template>
    <ds-modal
        is-open
        data-qa="rescheduleBroadcastDialog"
        show-close
        :title="$options.MESSAGE_CONSTANTS.RESCHEDULE_MODAL_TITLE"
        :sub-title="subTitle"
        @close="modalClose"
    >
        <div>
            <div class="reschedule-description">
                {{ $options.MESSAGE_CONSTANTS.RESCHEDULE_MODAL_DESCRIPTION }}
            </div>

            <div class="date-holder">
                <ds-date-picker
                    v-model="date"
                    data-qa="reschedule-date-picker"
                    class="reschedule-date-picker"
                    disable-before-date="today"
                />

                <ds-date-picker
                    v-model="time"
                    data-qa="reschedule-time-picker"
                    class="reschedule-time-picker"
                    time-only
                />

                <ds-multiselect
                    v-model="selectedTimeZone"
                    data-qa="time-zone-picker"
                    class="timezone-dropdown"
                    placeholder="Choose timezone to send"
                    searchable
                    :options="validTimeZones"
                    bind-value-only
                />
            </div>

            <div v-if="isInPast" class="date-warning">
                <ds-inline-alert data-qa="past-message-warning" leading-icon type="warning">
                    {{ $options.MESSAGE_CONSTANTS.PAST_MESSAGE_WARNING }}
                </ds-inline-alert>
            </div>

            <div class="reschedule-buttons-container">
                <ds-button
                    :loading="loading"
                    data-qa="confirm-button"
                    class="reschedule-date"
                    @click="reschedule"
                >
                    {{ $options.MESSAGE_CONSTANTS.CONFIRM_BUTTON }}
                </ds-button>

                <ds-text-button @click="modalClose">
                    {{ $options.MESSAGE_CONSTANTS.CANCEL_BUTTON }}
                </ds-text-button>
            </div>
        </div>
    </ds-modal>
</template>

<script>
import { getLocalizationInfo } from '../../../api/broadcasts/emailBroadcast';
import { isScheduleDatePast } from '../utils/emailBroadcast.util';
import {
    formattedDateTime,
    get24HourTime,
    tomorrowsDate,
} from '../utils/date.util';

import { CONTACT_TIME_ZONE_OPTION } from '../constants/emailBroadcast.constants';

export const MESSAGE_CONSTANTS = {
    PAST_MESSAGE_WARNING: 'You have selected a date and time in the past. If you continue, your broadcast will be sent immediately.',
    RESCHEDULE_MODAL_TITLE: 'Reschedule email broadcast',
    RESCHEDULE_MODAL_DESCRIPTION: 'When do you want to send this broadcast?',
    CONFIRM_BUTTON: 'Reschedule',
    CANCEL_BUTTON: 'Close',
};

export default {
    MESSAGE_CONSTANTS,
    props: {
        userLocale: {
            type: String,
            default: '',
        },
        subTitle: String,
    },

    data() {
        return {
            appTimeZone: '',
            date: new Date(),
            time: '0000',
            validTimeZones: [],
            loading: false,
            selectedTimeZone: CONTACT_TIME_ZONE_OPTION,
        };
    },

    async created() {
        this.date = tomorrowsDate();
        this.time = get24HourTime();
        this.loading = true;

        const {
            timeZoneOptions: unorganizedTimeZones,
            localizationInfo,
        } = await getLocalizationInfo();

        this.loading = false;

        this.validTimeZones = Object.keys(unorganizedTimeZones).map((timeZoneKey) => {
            return { label: unorganizedTimeZones[timeZoneKey], value: timeZoneKey };
        });

        this.validTimeZones.push({
            label: 'Use Contact Time Zone',
            value: CONTACT_TIME_ZONE_OPTION,
        });

        this.appTimeZone = localizationInfo.appTimeZone;
    },

    computed: {
        isInPast() {
            const timeZoneId = this.useContactTimeZone ? this.appTimeZone : this.selectedTimeZone;

            return this.time && isScheduleDatePast({
                scheduleDate: formattedDateTime(this.date, this.time, this.userLocale),
                useContactTimeZone: this.useContactTimeZone,
                timeZoneId,
            });
        },

        useContactTimeZone() {
            return this.selectedTimeZone === CONTACT_TIME_ZONE_OPTION;
        },
    },

    methods: {
        reschedule() {
            this.loading = true;
            this.$emit('reschedule', formattedDateTime(this.date, this.time, this.userLocale), this.selectedTimeZone);
        },

        modalClose() {
            this.$emit('close');
        },
    },
};
</script>

<style lang="scss">
    .reschedule-description {
        margin-bottom: 1rem;
    }

    .timezone-dropdown {
        --input-padding-top: 0;
    }

    .reschedule-time-picker {
        width: 300px;
        margin-right: 1rem;
    }

    .reschedule-date-picker {
        width: 500px;
        margin-right: 1rem;
    }

    .reschedule-date {
      margin-right: 1rem;
    }

    .date-warning {
         margin-bottom: 1.5rem;
     }

    .date-holder {
      display: flex;
    }

    .reschedule-buttons-container {
        display: flex;
    }
</style>
